import { createContext, useState, ReactNode, useContext } from 'react'
import { Banco, BancoData, ClienteData, ContadorData, FeedbackMessage } from '@/types/client'

interface ClientContextType {
  clientMode: 'new-owner' | 'existing-owner' | null
  setClientMode: (value: 'new-owner' | 'existing-owner' | null) => void
  // CNPJ data
  cnpj: string
  setCnpj: (value: string) => void
  informacoes: any | null
  setInformacoes: (value: any) => void
  informacoesCnpj: any | null
  setInformacoesCnpj: (value: any) => void

  // Loading state
  carregando: boolean
  setCarregando: (value: boolean) => void

  // Client info
  clienteData: ClienteData | null
  setClienteData: (data: ClienteData | null) => void

  // Contador info
  contadorData: ContadorData
  setContadorData: (data: ContadorData) => void

  // Bank info
  bancoData: BancoData
  setBancoData: (data: BancoData) => void
  bancos: Banco[]
  setBancos: (data: Banco[]) => void

  // Regime and products
  regimeTributario: string | null
  setRegimeTributario: (value: string | null) => void
  produtosSelecionados: string[]
  setProdutosSelecionados: (value: string[]) => void

  // Other form fields
  customComissao: string
  setCustomComissao: (value: string) => void
  senhaProprietario: string
  setSenhaProprietario: (value: string) => void
  comissaoEscrita: string
  setComissaoEscrita: (value: string) => void
  possui: boolean
  setPossui: (value: boolean) => void
  concordo: boolean
  setConcordo: (value: boolean) => void

  // UI state
  mostrarCampos: boolean
  setMostrarCampos: (value: boolean) => void
  mostrarCamposContador: boolean
  setMostrarCamposContador: (value: boolean) => void
  showAssinarPopup: boolean
  setShowAssinarPopup: (value: boolean) => void
  showEditarPopup: boolean
  setShowEditarPopup: (value: boolean) => void

  // Process state
  verificacaoConcluida: boolean
  setVerificacaoConcluida: (value: boolean) => void
  isMinutaEnviada: boolean
  setIsMinutaEnviada: (value: boolean) => void
  isClienteCriado: boolean
  setIsClienteCriado: (value: boolean) => void

  // Feedback
  isDialogOpen: boolean
  setIsDialogOpen: (value: boolean) => void
  message: FeedbackMessage | null
  setMessage: (value: FeedbackMessage | null) => void

  // Helper derived values
  id_client: string
  podeCriarCliente: boolean
}

const ClientContext = createContext<ClientContextType | undefined>(undefined)

export function ClientProvider({ children }: { children: ReactNode }) {
  const [clientMode, setClientMode] = useState<'new-owner' | 'existing-owner' | null>(null)
  const [cnpj, setCnpj] = useState('')
  const [informacoes, setInformacoes] = useState<any>(null)
  const [informacoesCnpj, setInformacoesCnpj] = useState<any>(null)
  const [carregando, setCarregando] = useState(false)
  const [regimeTributario, setRegimeTributario] = useState<string | null>(null)
  const [produtosSelecionados, setProdutosSelecionados] = useState<string[]>([])
  const [concordo, setConcordo] = useState(false)
  const [possui, setPossui] = useState(false)
  const [clienteData, setClienteData] = useState<ClienteData | null>(null)
  const [bancoData, setBancoData] = useState({
    banco: '',
    agencia: '',
    conta: ''
  })
  const [bancos, setBancos] = useState<Banco[]>([])
  const [customComissao, setCustomComissao] = useState('')
  const [senhaProprietario, setSenhaProprietario] = useState('')
  const [comissaoEscrita, setComissaoEscrita] = useState('')
  const [showAssinarPopup, setShowAssinarPopup] = useState(false)
  const [showEditarPopup, setShowEditarPopup] = useState(false)
  const [verificacaoConcluida, setVerificacaoConcluida] = useState(false)
  const [isMinutaEnviada, setIsMinutaEnviada] = useState(false)
  const [isClienteCriado, setIsClienteCriado] = useState(false)
  const [contadorData, setContadorData] = useState({
    name: '',
    whatsapp: '',
    email: ''
  })
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<FeedbackMessage | null>(null)
  const [mostrarCampos, setMostrarCampos] = useState(false)
  const [mostrarCamposContador, setMostrarCamposContador] = useState(false)

  const getDigits = (value?: string | null) => String(value ?? '').replace(/[^0-9]/g, '')

  // Computed values
  const id_client =
    getDigits(cnpj) ||
    getDigits(informacoesCnpj?.cnpjcpf) ||
    getDigits(informacoesCnpj?.cnpj) ||
    getDigits(informacoesCnpj?.cpf) ||
    getDigits(clienteData?.cpf)
  const podeCriarCliente = Boolean(clienteData && contadorData)

  const value = {
    clientMode,
    setClientMode,
    cnpj,
    setCnpj,
    informacoes,
    setInformacoes,
    informacoesCnpj,
    setInformacoesCnpj,
    carregando,
    setCarregando,
    regimeTributario,
    setRegimeTributario,
    produtosSelecionados,
    setProdutosSelecionados,
    concordo,
    setConcordo,
    possui,
    setPossui,
    clienteData,
    setClienteData,
    bancoData,
    setBancoData,
    bancos,
    setBancos,
    customComissao,
    setCustomComissao,
    senhaProprietario,
    setSenhaProprietario,
    comissaoEscrita,
    setComissaoEscrita,
    showAssinarPopup,
    setShowAssinarPopup,
    showEditarPopup,
    setShowEditarPopup,
    verificacaoConcluida,
    setVerificacaoConcluida,
    isMinutaEnviada,
    setIsMinutaEnviada,
    isClienteCriado,
    setIsClienteCriado,
    contadorData,
    setContadorData,
    isDialogOpen,
    setIsDialogOpen,
    message,
    setMessage,
    mostrarCampos,
    setMostrarCampos,
    mostrarCamposContador,
    setMostrarCamposContador,
    id_client,
    podeCriarCliente
  }

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
}

export const useClientContext = () => {
  const context = useContext(ClientContext)
  if (context === undefined) {
    throw new Error('useClientContext must be used within a ClientProvider')
  }
  return context
}
